/**
 * DashboardCanvas — Grid der Dashboard-Widgets mit festem Layout.
 *
 * Wiederverwendet WidgetCard/WIDGETS des Admin-Dashboards. Zwei Modi:
 *  - `editable`: Karten sind verschiebbar und skalierbar (Abteilungs-Editor)
 *  - read-only:  reine Anzeige, kein Ziehen, keine Größenänderung (Mitglieder)
 * Das Layout kommt von außen (`config`), gespeichert wird hier nichts.
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GridLayout, useContainerWidth, type Layout, type LayoutItem, type GridLayoutProps } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DENSITY_VALUES, getThemeColors } from '../../../lib/theme';
import { useIsMobile } from '../../../lib/useBreakpoint';
import type { Density } from '../../../types';
import type { DashboardConfig, DashboardLayoutItem } from '../../../lib/api';
import { useDashboardData } from './useDashboardData';
import { WIDGETS, effectiveDashboardLayout } from './helpers';
import { WidgetCard } from './WidgetCard';
import { ErrorBanner } from './ErrorBanner';

interface DashboardCanvasProps {
  dark: boolean;
  density: Density;
  config: DashboardConfig;
  setPage: (page: string) => void;
  editable?: boolean;
  onLayoutChange?: (layout: DashboardLayoutItem[]) => void;
}

/** Ein Widget ist in einer Abteilung nur sichtbar, wenn es ausdrücklich eingeschaltet wurde. */
export function isWidgetOn(config: DashboardConfig, id: string): boolean {
  return config.widgets?.[id]?.visible === true;
}

/** Hat der Admin für die Abteilung ein Dashboard zusammengestellt? */
export function hasDepartmentDashboard(config: DashboardConfig | null | undefined): boolean {
  if (!config) return false;
  return WIDGETS.some(w => isWidgetOn(config, w.id));
}

export function DashboardCanvas({ dark, density, config, setPage, editable = false, onLayoutChange }: DashboardCanvasProps) {
  const t = getThemeColors(dark);
  const d = DENSITY_VALUES[density];
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();
  const { data, dataError } = useDashboardData();
  const { width: gridWidth, containerRef, mounted } = useContainerWidth({ measureBeforeMount: true, initialWidth: 1240 });

  const layout = effectiveDashboardLayout(config.layout);
  const visibleWidgets = WIDGETS.filter(w => isWidgetOn(config, w.id));
  const modeOf = (id: string) => config.widgets?.[id]?.mode ?? 'full';

  const handleLayoutChange = useCallback((next: Layout) => {
    if (!editable || !onLayoutChange) return;
    onLayoutChange((next as unknown as LayoutItem[]).map(l => ({ i: l.i, x: l.x, y: l.y, w: l.w, h: l.h })));
  }, [editable, onLayoutChange]);

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
        {dataError && <ErrorBanner dark={dark} message={dataError} />}
        {visibleWidgets.map(w => (
          <WidgetCard key={w.id} widget={w} mode={modeOf(w.id)} dark={dark} density={density} data={data} setPage={setPage} tr={tr} />
        ))}
      </div>
    );
  }

  const gap = typeof d.gap === 'number' ? d.gap : 12;
  const rglLayout: LayoutItem[] = layout
    .filter(l => isWidgetOn(config, l.i))
    .map(l => {
      const def = WIDGETS.find(w => w.id === l.i);
      return {
        i: l.i, x: l.x, y: l.y, w: l.w, h: l.h,
        minW: def?.minW ?? 2, minH: def?.minH ?? 2,
        // Read-only: Karte ist fest.
        static: !editable,
      };
    });

  const gridConfig: GridLayoutProps['gridConfig'] = {
    cols: 12, rowHeight: 60, margin: [gap, gap] as const, containerPadding: [0, 0] as const,
  };
  const dragConfig: GridLayoutProps['dragConfig'] = { enabled: editable, handle: '.widget-drag-handle' };
  const resizeConfig: GridLayoutProps['resizeConfig'] = { enabled: editable };

  return (
    <div>
      {dataError && <ErrorBanner dark={dark} message={dataError} />}
      <div ref={containerRef as React.RefObject<HTMLDivElement>} style={{ width: '100%' }}>
        {!mounted ? (
          <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>{tr('dashboard.page.loading_config')}</div>
        ) : (
          <GridLayout
            layout={rglLayout as unknown as Layout}
            width={gridWidth}
            gridConfig={gridConfig}
            dragConfig={dragConfig}
            resizeConfig={resizeConfig}
            onLayoutChange={handleLayoutChange}
            className="admin-dashboard-grid"
            style={{ minHeight: 200 }}
          >
            {visibleWidgets.map(w => (
              <div key={w.id} style={{ overflow: 'hidden' }}>
                <WidgetCard
                  widget={w} mode={modeOf(w.id)} dark={dark} density={density}
                  data={data} setPage={setPage} tr={tr} draggable={editable}
                />
              </div>
            ))}
          </GridLayout>
        )}
      </div>
    </div>
  );
}
